import * as React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { FormFeedback, Input } from 'reactstrap';

import { BaseFormModel } from '@app/Services/BaseFormModel';
import { Icon } from '@app/Components/Icon';
import { extensions } from '@app/Services/Extensions';
import { RemoteOptionsEntry, RemoteOptionsStore } from './RemoteOptionsStore';

import './_remote-suggest-input.scss';

// The text input owns the raw key at all times and the option list is only a suggestion source, so
// a key that does not exist upstream yet stays visible, editable and saveable.
const MAX_SUGGESTIONS = 500;
const SEARCH_DEBOUNCE_MS = 350;

export type RemoteSuggestInputProps<T extends BaseFormModel> = {
    formModel: T;
    name: keyof T;
    optionsUrl?: string;
    formNamePrefix?: string;
    placeholder?: string;
    disabled?: boolean;
};

@observer
export class RemoteSuggestInput<T extends BaseFormModel> extends React.Component<RemoteSuggestInputProps<T>, {}> {
    private readonly _entry: RemoteOptionsEntry | null;
    private readonly _listId: string;

    constructor(props: RemoteSuggestInputProps<T>) {
        super(props);
        this._entry = props.optionsUrl ? RemoteOptionsStore.get(props.optionsUrl) : null;
        this._listId = `suggest_${props.formNamePrefix ?? ''}_${props.name as string}`.replace(/[^\w-]/g, '_');
    }

    public componentDidMount() {
        void this._entry?.load(this._value);
    }

    public render() {
        const { formModel, name, formNamePrefix, placeholder, disabled } = this.props;
        const options = this._entry?.options ?? [];

        return (
            <>
                <Input
                    type="text"
                    name={(formNamePrefix ?? '') + (name as string)}
                    value={this._value}
                    list={options.length ? this._listId : undefined}
                    autoComplete="off"
                    disabled={disabled}
                    placeholder={placeholder ?? this._placeholder}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onChange={this._changeHandler}
                />
                {!!options.length &&
                    <datalist id={this._listId}>
                        {options.slice(0, MAX_SUGGESTIONS).map((option, index) =>
                            <option
                                key={`${option.value}_${index}`}
                                value={option.value}
                                label={this._suggestionLabel(option.label, option.status, option.value)}
                            />
                        )}
                    </datalist>
                }
                {this._renderStatus()}
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </>
        );
    }

    private get _value(): string {
        return (this.props.formModel.getValue(this.props.name) ?? '') as string;
    }

    private get _placeholder(): string | undefined {
        const entry = this._entry;
        if (!entry?.isSearchable) return undefined;

        return `Type at least ${entry.searchMinLength} characters to search`;
    }

    private _suggestionLabel(label: string, status?: string, value?: string): string | undefined {
        const text = status ? `${label} (${status})` : label;

        return text === value ? undefined : text;
    }

    @action.bound
    private _changeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        this.props.formModel.setValue(this.props.name, value);

        if (!this._entry?.isSearchable) return;

        // executeTimeout stores the pending timer on the callback itself, so this must stay a stable
        // per instance reference - an inline arrow would never actually debounce.
        extensions.executeTimeout(this._runSearch, SEARCH_DEBOUNCE_MS, this);
    }

    private _runSearch = () => {
        const value = this._value;
        void this._entry?.search(value, value);
    };

    private _renderStatus() {
        const entry = this._entry;
        if (!entry) return null;

        // Gating on isLoaded rather than a pending flag: componentDidMount runs after the first paint,
        // so nothing is pending there yet and the warning would flash on every mount.
        if (!entry.isLoaded) {
            return (
                <div className="remote-suggest__hint">
                    <Icon name="spinner" className="fa-spin mr-1" />Loading options&hellip;
                </div>
            );
        }

        if (entry.loadError) {
            return <div className="remote-suggest__error">{entry.loadError}{this._renderReload()}</div>;
        }

        const value = this._value;
        if (!value) return null;

        const option = entry.findOption(value);
        if (option) {
            return (
                <div className="remote-suggest__hint">
                    {option.label}
                    {!!option.status && <span className="remote-suggest__badge">{option.status}</span>}
                    {!!option.hint && <>&nbsp;<Icon name="question-circle" title={option.hint} /></>}
                    {this._renderReload()}
                </div>
            );
        }

        if (entry.isSearching) {
            return (
                <div className="remote-suggest__hint">
                    <Icon name="spinner" className="fa-spin mr-1" />Searching&hellip;
                </div>
            );
        }

        if (!entry.isKnownMissing(value)) {
            // A searchable source has not been asked about this key, so nothing can be claimed about it.
            return <div className="remote-suggest__hint">Display name unknown &mdash; not looked up.{this._renderReload()}</div>;
        }

        return (
            <div className="remote-suggest__warning">
                <Icon name="exclamation-triangle" className="mr-1" />
                {entry.isTruncated
                    ? 'Not in the loaded part of the list - will be saved as typed.'
                    : 'Not in the list - will be saved as typed.'}
                {this._renderReload()}
            </div>
        );
    }

    private _renderReload() {
        return (
            <Icon
                name="sync"
                className="remote-suggest__reload"
                title="Reload option list"
                onClick={() => void this._entry?.reload(this._value)}
            />
        );
    }
}
